import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Wallet } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogTitle,
} from '@ncoder/ui';
import { cn } from '@/lib/utils';
import { formatCurrencyUsd } from '@/components/organisms/org-admin/usageFormat';

/**
 * Buys credit into the organization's pool. No member is named — that is the whole point of the
 * pool, and naming one here would collapse it back into a top-up.
 *
 * Charges a card, so it carries the same three defences as every other paying dialog in this
 * module: disabled while pending, no automatic retry, and an ambiguous network failure reported as
 * "we don't know" with no retry offered.
 */

export interface BuyCreditsDialogProps {
  open: boolean;
  hasPaymentMethod: boolean;
  /** Extra credit per dollar, from `GET /billing`. Never defaulted — see `TopUpDialog`. */
  bonusMultiplier?: number;
  currentBalanceUsd: number;
  isSubmitting: boolean;
  errorMessage: string | null;
  isAmbiguous: boolean;
  onSubmit: (amountUsd: number) => void;
  onClose: () => void;
}

const PRESETS = [100, 250, 500, 1000];

export function BuyCreditsDialog({
  open,
  hasPaymentMethod,
  bonusMultiplier,
  currentBalanceUsd,
  isSubmitting,
  errorMessage,
  isAmbiguous,
  onSubmit,
  onClose,
}: BuyCreditsDialogProps) {
  const [raw, setRaw] = useState('250');

  useEffect(() => {
    if (open) setRaw('250');
  }, [open]);

  const amount = Number(raw);
  const valid = Number.isFinite(amount) && amount > 0;
  const hasBonus = typeof bonusMultiplier === 'number' && bonusMultiplier > 1;
  const granted = hasBonus ? amount * bonusMultiplier : amount;
  const bonusPercent = hasBonus ? Math.round((bonusMultiplier - 1) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !isSubmitting && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          {/* One verb for both purchases, differentiated by TARGET. "Buy credit" here and "Top up
              budget" on a member row were the same currency under two nouns, which hid the thing that
              is actually the same and obscured the thing that is actually different: where the money
              lands. */}
          <DialogTitle className="text-base font-semibold">Add organization credit</DialogTitle>
          <DialogDescription className="text-[13px]">
            Credit goes to the organization, not a person. Assign it to members whenever you like.
          </DialogDescription>
        </DialogHeader>

        <DialogMain className="space-y-4">
          {hasBonus && (
            <p className="oa-meta">
              Credit is bought with{' '}
              <span className="font-semibold text-success-strong">{bonusPercent}% extra</span> —
              {/* Stated as a worked example, because a multiplier is an abstraction and a pair of
                  dollar figures is not. */}{' '}
              pay $100, get {formatCurrencyUsd(100 * bonusMultiplier!)} of credit.
            </p>
          )}

          <label className="block">
            <span className="oa-micro mb-1.5 block">Amount (USD)</span>
            <input
              type="number"
              inputMode="decimal"
              min={1}
              value={raw}
              disabled={isSubmitting}
              onChange={(event) => setRaw(event.target.value)}
              className={cn(
                'oa-interactive h-9 w-full rounded-md border bg-background px-2.5 text-[13px] tabular-nums',
                valid ? 'border-border/70' : 'border-destructive',
              )}
            />
          </label>

          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                disabled={isSubmitting}
                onClick={() => setRaw(String(preset))}
                className="oa-interactive h-7 rounded-md border border-border/70 px-2.5 text-[12px] font-medium tabular-nums hover:bg-muted/50"
              >
                ${preset}
              </button>
            ))}
          </div>

          {valid && (
            <div className="rounded-[var(--oa-radius-md)] border border-border/60 bg-muted/30 p-3 text-[13px]">
              <p>
                {hasPaymentMethod ? 'Your card will be charged ' : 'You will pay '}
                <span className="font-semibold tabular-nums">{formatCurrencyUsd(amount)}</span>
                {hasBonus ? (
                  <>
                    , adding{' '}
                    <span className="font-semibold tabular-nums text-success-strong">
                      {formatCurrencyUsd(granted)}
                    </span>{' '}
                    of credit.
                  </>
                ) : (
                  <> of credit.</>
                )}{' '}
                {/* The resulting balance, not just the delta — it is the number the admin will
                    check against what they are about to assign. */}
                Balance becomes{' '}
                <span className="font-semibold tabular-nums">
                  {formatCurrencyUsd(currentBalanceUsd + granted)}
                </span>
                .
              </p>
            </div>
          )}

          {isAmbiguous ? (
            <div
              role="alert"
              className="flex gap-2.5 rounded-[var(--oa-radius-md)] border border-warning/30 bg-warning/[0.07] p-3"
            >
              <AlertTriangle className="mt-px h-4 w-4 shrink-0 text-warning-strong" aria-hidden />
              <p className="text-[13px] leading-snug">{errorMessage}</p>
            </div>
          ) : (
            errorMessage && (
              <p role="alert" className="text-[13px] font-medium text-destructive-strong">
                {errorMessage}
              </p>
            )
          )}
        </DialogMain>

        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => onSubmit(amount)}
            disabled={!valid || isSubmitting || isAmbiguous}
          >
            {isSubmitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Wallet className="h-3.5 w-3.5" aria-hidden />
            )}
            {isSubmitting ? 'Charging…' : valid ? `Add ${formatCurrencyUsd(amount)}` : 'Add credit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
